import { lazy, Suspense } from "react";
import type { ComponentType, ReactNode } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
export function lazyNamed<P extends object, M extends Record<string, unknown>>(load: () => Promise<M>, exportName: keyof M & string): ComponentType<P> {
  return lazy(async () => {
    const loaded = await load();
    return {
      default: loaded[exportName] as ComponentType<P>
    };
  });
}
export function LazyOverlayBoundary({
  name,
  fallback,
  children
}: {
  name: string;
  fallback: ReactNode;
  children: ReactNode;
}) {
  return <ErrorBoundary tags={{
    surface: "lazyOverlay",
    action: name
  }} reportLevel="warning" fallback={() => fallback} data-sentry-element="ErrorBoundary" data-sentry-component="LazyOverlayBoundary" data-sentry-source-file="createLazyOverlay.tsx">
            {children}
        </ErrorBoundary>;
}
export function createLazyOverlay<P extends object, M extends Record<string, unknown> = Record<string, unknown>>({
  name,
  load,
  exportName,
  renderFallback
}: {
  name: string;
  load: () => Promise<M>;
  exportName: keyof M & string;
  renderFallback: (props: P) => ReactNode;
}): ComponentType<P> {
  const Panel = lazyNamed<P, M>(load, exportName);
  return function LazyOverlay(props: P) {
    return <LazyOverlayBoundary name={name} fallback={renderFallback(props)}>
                <Suspense fallback={renderFallback(props)}>
                    <Panel {...props} />
                </Suspense>
            </LazyOverlayBoundary>;
  };
}